/**
 * `throwSettlement` — "have THIS throw's dice stopped rolling yet?", as a promise.
 *
 * Without it the client discloses an attack's outcome while its dice are still airborne: the chat
 * result card and the board's combat animation both fire the moment the resolution broadcast
 * arrives, and only afterwards do the dice land. Holding the reveal needs a completion signal, so
 * this module is that signal — kept pure and headlessly testable for the same reason
 * {@link ./dicePlayback} is: {@link ./DiceStage} needs a real WebGL context to construct, so
 * nothing that lives inside it can be proven.
 *
 * Two pieces, and the split matters:
 *
 *  - a **ticket** is one awaitable throw, handed to whoever might visualize it;
 *  - a **ledger** maps the stage's own batch ids ({@link ./dicePlayback}) onto the tickets waiting
 *    on them, so "batch 7 settled" releases exactly the caller that threw batch 7 — a barrage's
 *    two shots resolve independently and in their own order, not both at the end.
 *
 * **The claim protocol is what keeps a missing visualizer from hanging the app.** A throw is
 * emitted over a fire-and-forget module pub/sub whose isolation is deliberate and load-bearing
 * (`DiceFeedSubscriber.tsx`: "Remove this one component and COMMS.app + the feed are fully
 * functional"), so the thrower cannot know whether anything is listening, whether the listener
 * has a stage, or whether the throw was over the visualization cap and will never tumble. So a
 * ticket starts **unclaimed**: a visualizer that is really going to land dice calls
 * {@link ThrowTicket.claim} synchronously during the emit, and the thrower settles any ticket
 * still unclaimed when the emit returns. No dice ⇒ no wait, by construction rather than by
 * timeout.
 *
 * The ceiling is the backstop under that, not the mechanism: every ticket also settles itself
 * after {@link SETTLE_CEILING_MS} no matter what. A held card that never releases is a far worse
 * failure than one that releases early, and the ceiling means no bug in the visualizer — a
 * dropped batch, a stage disposed mid-flight, a callback that stops firing — can wedge an
 * outcome off the screen for the rest of the session.
 */

/**
 * The hard ceiling on any single hold, and it is **derived from the recording budget, not tuned**.
 *
 * A tumble is pure playback of a pre-recorded buffer: `recordThrowBatch` force-settles every die
 * at `SIM_BUDGET_FRAMES` (200) recorded frames, and `DiceStage` plays those back at
 * `RECORD_FPS * PLAYBACK_RATE` (120) frames per real second — so **no throw can tumble longer
 * than 200/120 = 1667ms**, ever, for any pool shape. Measured against that ceiling over 700
 * headless throws across seven real pool shapes: p50 0.75s, p90 1.11s, max 1.67s, the max being
 * exactly the budget cutoff.
 *
 * 2500ms is that structural maximum plus ~50% slack for the things playback rides on but the
 * recording doesn't model — rAF scheduling, a backgrounded tab's throttled clock, a frame-starved
 * machine mid-encounter. If a hold ever hits this, something is broken, and the ceiling's job is
 * to make that a late card rather than a lost one.
 */
export const SETTLE_CEILING_MS = 2500;

/** One awaitable throw. Handed to the visualizer; the thrower keeps the promise. */
export type ThrowTicket = {
  /**
   * "I will land dice for this throw and I will settle it." Called synchronously by a visualizer
   * during the emit — see the claim protocol in the file header. Idempotent.
   */
  claim: () => void;
  /** Release the awaiting caller. Idempotent, and safe to call on an unclaimed ticket. */
  settle: () => void;
  /** Whether any visualizer took responsibility for settling this throw. */
  readonly claimed: boolean;
  /** Whether this throw has already been released (by settle, or by the ceiling). */
  readonly settled: boolean;
};

/**
 * A ticket and the promise that resolves when it settles. `ceilingMs` overrides
 * {@link SETTLE_CEILING_MS} (tests pass a small one); a non-positive ceiling disables the timer
 * entirely, which is only for a test that wants to prove the ceiling is what released a ticket.
 */
export function createThrowTicket(ceilingMs: number = SETTLE_CEILING_MS): {
  ticket: ThrowTicket;
  done: Promise<void>;
} {
  let claimed = false;
  let settled = false;
  let resolve!: () => void;
  const done = new Promise<void>((r) => {
    resolve = r;
  });
  const timer =
    ceilingMs > 0
      ? setTimeout(() => {
          if (settled) return;
          settled = true;
          resolve();
        }, ceilingMs)
      : undefined;
  const ticket: ThrowTicket = {
    claim: () => {
      claimed = true;
    },
    settle: () => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      resolve();
    },
    get claimed() {
      return claimed;
    },
    get settled() {
      return settled;
    },
  };
  return { ticket, done };
}

/**
 * The batch id → ticket map a visualizer keeps beside its stage. One ledger per stage lifetime:
 * batch ids are only unique within one {@link ./dicePlayback} playback, and a rebuilt stage
 * starts counting again from zero.
 */
export type BatchLedger = {
  /** Wait for `batchId` to settle before releasing `ticket`. */
  register: (batchId: number, ticket: ThrowTicket) => void;
  /** `batchId`'s dice have stopped rolling — release its waiter and forget the batch. */
  settle: (batchId: number) => void;
  /**
   * Release **every** waiter and empty the ledger — the stage is going away (disposed, rebuilt
   * after a context loss) and those batches will never report a settle. Abandoning a wait must
   * release it, not orphan it.
   */
  settleAll: () => void;
  /** How many throws are still being waited on. Diagnostic — a healthy ledger drains to 0. */
  readonly pending: number;
};

export function createBatchLedger(): BatchLedger {
  const waiting = new Map<number, ThrowTicket>();
  return {
    register: (batchId, ticket) => {
      // A batch id is handed out once per stage, so this never collides in practice; if it
      // somehow did, releasing the incumbent is the safe direction (an early card, not a stuck
      // one) — the same instinct as the ceiling.
      const incumbent = waiting.get(batchId);
      if (incumbent !== undefined) incumbent.settle();
      waiting.set(batchId, ticket);
    },
    settle: (batchId) => {
      const ticket = waiting.get(batchId);
      if (ticket === undefined) return;
      waiting.delete(batchId);
      ticket.settle();
    },
    settleAll: () => {
      for (const ticket of waiting.values()) ticket.settle();
      waiting.clear();
    },
    get pending() {
      return waiting.size;
    },
  };
}
